import {
  InvoiceBusinessState,
  ContractBilling,
  InvoiceSubkind,
  PaymentsGroupStateType,
  ChargeState,
  CreatedByUserEnum,
  PayoutViewState,
} from "@oysterhr/schema"
import { type BadgeStateItem } from "~/shared/components/StatesDescription"

export const InvoiceSubkindLabelsMap: Record<
  InvoiceSubkind,
  Pick<BadgeStateItem, "translationKey">
> = {
  [InvoiceSubkind.ContractorAdHocPayment]: {
    translationKey: "subkind.contractor_ad_hoc_payment",
  },
  [InvoiceSubkind.ContractorExpenseReimbursement]: {
    translationKey: "subkind.contractor_expense_reimbursement",
  },
  [InvoiceSubkind.ContractorMonthlyPayment]: {
    translationKey: "subkind.contractor_monthly_payment",
  },
  [InvoiceSubkind.OysterContractorsFee]: {
    translationKey: "subkind.oyster_contractors_fee",
  },
  [InvoiceSubkind.OysterDeposit]: {
    translationKey: "subkind.oyster_deposit",
  },
  [InvoiceSubkind.OysterManualInvoice]: {
    translationKey: "subkind.oyster_manual_invoice",
  },
  [InvoiceSubkind.OysterPayroll]: {
    translationKey: "subkind.oyster_payroll",
  },
  [InvoiceSubkind.OysterPrefunding]: {
    translationKey: "subkind.oyster_prefunding",
  },
  [InvoiceSubkind.OysterSettlement]: {
    translationKey: "subkind.oyster_settlement",
  },
  [InvoiceSubkind.OysterTrueUps]: {
    translationKey: "subkind.oyster_true_ups",
  },
  [InvoiceSubkind.OysterScale]: {
    translationKey: "subkind.oyster_scale",
  },
  [InvoiceSubkind.OysterPlatformPlans]: {
    translationKey: "subkind.oyster_platform_plans",
  },
  [InvoiceSubkind.OysterSalaryInsights]: {
    translationKey: "subkind.oyster_salary_insights",
  },
  [InvoiceSubkind.OysterGlobalPayroll]: {
    translationKey: "subkind.oyster_global_payroll",
  },
}

export type ClientInvoiceState = Exclude<
  InvoiceBusinessState,
  InvoiceBusinessState.Paid | InvoiceBusinessState.Processing
>

export const InvoiceBusinessStateLabelsMap: Record<ClientInvoiceState, BadgeStateItem> = {
  [InvoiceBusinessState.WaitingApproval]: {
    variation: "warning",
    translationKey: "states.waitingApproval",
    descriptionKey: "statesDescription.waitingApproval",
  },
  [InvoiceBusinessState.ReadyForReview]: {
    variation: "info",
    translationKey: "states.readyForReview",
    descriptionKey: "statesDescription.readyForReview",
  },
  [InvoiceBusinessState.Approved]: {
    variation: "success",
    translationKey: "states.approved",
    descriptionKey: "statesDescription.approved",
  },
  [InvoiceBusinessState.AutoApproved]: {
    variation: "success",
    translationKey: "states.autoApproved",
    descriptionKey: "statesDescription.autoApproved",
  },
  [InvoiceBusinessState.Disputed]: {
    variation: "error",
    translationKey: "states.disputed",
    descriptionKey: "statesDescription.disputed",
  },
  [InvoiceBusinessState.Draft]: {
    variation: "gray",
    translationKey: "states.draft",
    descriptionKey: "statesDescription.draft",
  },
  [InvoiceBusinessState.Voided]: {
    variation: "error",
    translationKey: "states.voided",
    descriptionKey: "statesDescription.voided",
  },
}

export const ContractBillingTypeLabelsMap: Record<
  ContractBilling,
  Pick<BadgeStateItem, "translationKey">
> = {
  [ContractBilling.Fixed]: { translationKey: "contractBillingType.fixed" },
  [ContractBilling.PayAsYouGo]: { translationKey: "contractBillingType.payAsYouGo" },
  [ContractBilling.Milestone]: { translationKey: "contractBillingType.milestone" },
}

export type ClientInvoicePaymentState = Exclude<ChargeState, ChargeState.AutomatedProcessConfirmed>

export const InvoicePaymentStateLabelsMap: Record<ClientInvoicePaymentState, BadgeStateItem> = {
  [ChargeState.Pending]: {
    variation: "warning",
    translationKey: "paymentStates.pending.title",
    descriptionKey: "paymentStates.pending.description",
  },
  [ChargeState.AutomatedProcessInProgress]: {
    variation: "warning",
    translationKey: "paymentStates.automatedProcessInProgress.title",
    descriptionKey: "paymentStates.automatedProcessInProgress.description",
  },
  [ChargeState.AutomatedProcessFailed]: {
    variation: "error",
    translationKey: "paymentStates.automatedProcessFailed.title",
    descriptionKey: "paymentStates.automatedProcessFailed.description",
  },
  [ChargeState.Paid]: {
    variation: "success",
    translationKey: "paymentStates.paid.title",
    descriptionKey: "paymentStates.paid.description",
  },
  [ChargeState.PartiallyPaid]: {
    variation: "success",
    translationKey: "paymentStates.partiallyPaid.title",
    descriptionKey: "paymentStates.partiallyPaid.description",
  },
  [ChargeState.ManualProcessNotPaid]: {
    variation: "warning",
    translationKey: "paymentStates.manualProcessNotPaid.title",
    descriptionKey: "paymentStates.manualProcessNotPaid.description",
  },
  [ChargeState.ChargedBack]: {
    variation: "error",
    translationKey: "paymentStates.chargeback.title",
    descriptionKey: "paymentStates.chargeback.description",
  },
  [ChargeState.Voided]: {
    variation: "error",
    translationKey: "paymentStates.voided.title",
    descriptionKey: "paymentStates.voided.description",
  },
  [ChargeState.NoPayment]: {
    variation: "gray",
    translationKey: "paymentStates.noPayment.title",
    descriptionKey: "paymentStates.noPayment.description",
  },
  [ChargeState.Overdue]: {
    variation: "gray",
    translationKey: "paymentStates.overdue.title",
    descriptionKey: "paymentStates.overdue.description",
  },
}

type FiltersMap = { [key in PayoutViewState]?: { translationKey: string } }

export const InvoicePayoutStateFiltersLabelsMap: FiltersMap = {
  [PayoutViewState.NoPayment]: {
    translationKey: "payoutStates.filters.noPayment",
  },
  [PayoutViewState.NotInitiated]: {
    translationKey: "payoutStates.filters.notInitiated",
  },
  [PayoutViewState.Initiated]: {
    translationKey: "payoutStates.filters.initiated",
  },
  [PayoutViewState.AutomatedProcessInProgress]: {
    translationKey: "payoutStates.filters.inProgress",
  },
  [PayoutViewState.Paid]: {
    translationKey: "payoutStates.filters.paid",
  },
  [PayoutViewState.Returned]: {
    translationKey: "payoutStates.filters.returned",
  },
}

export const InvoicePayoutStateLabelsMap: Record<PayoutViewState, BadgeStateItem> = {
  [PayoutViewState.NoPayment]: {
    variation: "gray",
    translationKey: "payoutStates.noPayment.title",
    descriptionKey: "payoutStates.noPayment.description",
  },
  [PayoutViewState.NotInitiated]: {
    variation: "gray",
    translationKey: "payoutStates.notInitiated.title",
    descriptionKey: "payoutStates.notInitiated.description",
  },
  [PayoutViewState.Initiated]: {
    variation: "info",
    translationKey: "payoutStates.initiated.title",
    descriptionKey: "payoutStates.initiated.description",
  },
  [PayoutViewState.AutomatedProcessInProgress]: {
    variation: "info",
    translationKey: "payoutStates.inProgress.title",
    descriptionKey: "payoutStates.inProgress.description",
  },
  [PayoutViewState.Paid]: {
    variation: "success",
    translationKey: "payoutStates.paid.title",
    descriptionKey: "payoutStates.paid.description",
  },
  [PayoutViewState.Sent]: {
    variation: "success",
    translationKey: "payoutStates.sent.title",
    descriptionKey: "payoutStates.sent.description",
  },
  [PayoutViewState.Returned]: {
    variation: "error",
    translationKey: "payoutStates.returned.title",
    descriptionKey: "payoutStates.returned.description",
  },
}

export const InvoicesBatchStateLabelsMap: Record<
  PaymentsGroupStateType,
  Omit<BadgeStateItem, "descriptionKey">
> = {
  // Initial state
  [PaymentsGroupStateType.Pending]: {
    variation: "warning",
    translationKey: "states.pending",
  },
  // Company admin confirms that money was sent
  [PaymentsGroupStateType.WireSent]: {
    variation: "info",
    translationKey: "states.wireSent",
  },
  // Intermediate state when staff marks invoices batch as paid or unpaid
  [PaymentsGroupStateType.Processing]: {
    variation: "info",
    translationKey: "states.processing",
  },
  // Oyster staff confirms that money was received
  [PaymentsGroupStateType.Paid]: {
    variation: "success",
    translationKey: "states.paid",
  },
}

export const InvoiceCreatedByLabelsMap: Record<
  CreatedByUserEnum,
  Omit<BadgeStateItem, "descriptionKey">
> = {
  [CreatedByUserEnum.Staff]: {
    variation: "info",
    translationKey: "createdBy.staff",
  },
  [CreatedByUserEnum.Company]: {
    variation: "success",
    translationKey: "createdBy.company",
  },
  [CreatedByUserEnum.Contractor]: {
    variation: "warning",
    translationKey: "createdBy.contractor",
  },
}
